import { Injectable, isDevMode } from '@angular/core';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { ProductsDaoService } from 'src/app/dao/catalogs-dao-services/products-dao-services/products-dao.service';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';
import { ProductBasic } from 'src/app/models/catalogs-model/product.model';
import { Subject } from 'rxjs/internal/Subject';
import { PriceInterface } from 'src/app/models/catalogs-model/price.interface';
import { TaxInterface } from 'src/app/models/catalogs-model/tax.interface';
import { PreparationBasic } from 'src/app/models/catalogs-model/preparation.model';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {

  private refreshProductsListener = new Subject<any>();
  private refreshProductListener = new Subject<any>();
  private filterProductsListener = new Subject<any>();
  private searchProductsListener = new Subject<any>();

  constructor(private userStorageService: UserStorageService,
              private productsDaoService: ProductsDaoService) { }

  public getProductsCounts() {
    return this.productsDaoService.getProductsCounts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          return {
            availableProducts: o['data']['products']['nbAvailables'],
            unavailableProducts: o['data']['products']['nbDisabled']
          };
        }
      })
    );
  }

  public getProductsList() {
    return this.productsDaoService.getProductsList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const products = new Array<ProductBasic>();
          o['data']['products'].forEach(
            (product) => {
              products.push(Object.assign(new ProductBasic(), product));
            }
          );
          return products;
        }
      })
    );
  }

  public getProductInfo(internalId) {
    return this.productsDaoService.getProductInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const product = new ProductBasic();
          Object.assign(product, o['data']['product']);
          product.PricePVP = this.parsePrice(o['data']['product']['pricePVP']);
          product.PricePVPOffer = this.parsePrice(o['data']['product']['pricePVPOffer']);
          product.TaxAmount = this.parseTax(o['data']['product']['taxAmount']);
          const preparations = new Array<PreparationBasic>();
          o['data']['product']['preparations'].forEach(p => preparations.push(Object.assign(new PreparationBasic(), p)));
          product.Preparations = preparations;
          return product;
        }
      })
    );
  }

  public addNewProduct(productForm, preparations) {
    return this.productsDaoService.addNewProduct(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), productForm.get('active').value, productForm.get('gtin').value, productForm.get('description').value, productForm.get('internalCode').value, productForm.get('tradename').value, productForm.get('category').value, productForm.get('categoryCode').value, productForm.get('additionalInfo').value, productForm.get('qty').value, productForm.get('magnitude').value, productForm.get('price').value ? this.unparsePrice(productForm.get('price').value.toString()) : null, productForm.get('offerPrice').value ? this.unparsePrice(productForm.get('offerPrice').value.toString()) : null, productForm.get('tax').value ? this.unparseTax(productForm.get('tax').value.toString()) : null, productForm.get('image').value, preparations).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public updateProductImage(internalId, imageUrl) {
    return this.productsDaoService.updateProductImage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, imageUrl).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public updateProductInfo(internalId, productInfoForm) {
    return this.productsDaoService.updateProductInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, productInfoForm.get('active').value, productInfoForm.get('gtin').value, productInfoForm.get('internalCode').value, productInfoForm.get('description').value, productInfoForm.get('tradename').value, productInfoForm.get('category').value, productInfoForm.get('categoryCode').value, productInfoForm.get('additionalInfo').value).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public updateProductPrices(internalId, productPricesForm) {
    return this.productsDaoService.updateProductPrices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, productPricesForm.get('quantity').value, productPricesForm.get('magnitude').value, this.unparsePrice(productPricesForm.get('price').value.toString()), this.unparsePrice(productPricesForm.get('offerPrice').value.toString()), this.unparseTax(productPricesForm.get('tax').value.toString())).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public updateProductPreparations(internalId, preparations) {
    const internalIds = preparations.map(p => p.InternalId);
    return this.productsDaoService.updateProductPreparations(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, internalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public deleteProducts(internalIds) {
    return this.productsDaoService.deleteProducts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public refreshProductsListen() {
    return this.refreshProductsListener.asObservable();
  }

  public refreshProduct() {
    this.refreshProductListener.next();
  }

  public refreshProductListen() {
    return this.refreshProductListener.asObservable();
  }

  public refreshProducts() {
    this.refreshProductsListener.next();
  }

  public filterProductsListen() {
    return this.filterProductsListener.asObservable();
  }

  public filterProducts() {
    this.filterProductsListener.next();
  }

  public searchProductsListen() {
    return this.searchProductsListener.asObservable();
  }

  public searchProducts(key) {
    this.searchProductsListener.next(key);
  }

  public unparsePrice(price: string): PriceInterface {
    const a = {
      significand: parseInt(price.replace(/[\.\,]/g, ''), 10),
      exponent: price.split(/[\.\,]/g).length > 1 ? price.split(/[\.\,]/g)[1].length * -1 : 0,
      currency: '€'
    };
    return a;
  }

  public parsePrice(priceInterface: PriceInterface) {
    return priceInterface.significand * Math.pow(10, priceInterface.exponent);
  }

  public unparseTax(tax: string): TaxInterface {
    const a = {
      significand: parseInt(tax.replace(/[\.\,]/g, ''), 10),
      exponent: tax.split(/[\.\,]/g).length > 1 ? tax.split(/[\.\,]/g)[1].length * -1 : 0
    };
    return a;
  }

  public parseTax(taxInterface: TaxInterface) {
    return taxInterface.significand * Math.pow(10, taxInterface.exponent);
  }
}
